/**
 * In-app updater: manifest parsing + version comparison.
 *
 * The app is sideloaded (not from Play), so it checks for its own new builds by
 * fetching a tiny JSON manifest published alongside each APK on a PUBLIC GitHub
 * Release (the chartplotter repo is private and has committed keys, so it can't
 * be the download host; see updateConfig.ts). The manifest says what the latest
 * build is and where its APK lives:
 *
 *   { "versionCode": 42, "versionName": "2026.07.20.42",
 *     "apkUrl": "https://github.com/.../app-debug.apk", "notes": "..." }
 *
 * Everything in this file is pure and Capacitor-free, so the compare-and-decide
 * logic is unit-testable headless. The native download + install and the fetch
 * live in updateStore.ts / the Android plugin. `versionCode` is the source of
 * truth for "is this newer" because it's a single monotonic integer that CI
 * bumps every build; versionName is only ever shown to the user.
 */

/** The published "latest build" manifest, once validated. */
export interface UpdateManifest {
  /** Monotonic build number. The ONLY field compared to decide "newer". */
  versionCode: number;
  /** Human-readable version, shown in the prompt (e.g. "2026.07.20.42"). */
  versionName: string;
  /** Absolute URL of the APK asset to download. */
  apkUrl: string;
  /** Optional release notes shown in the update prompt. */
  notes?: string;
}

/** The version the running app believes itself to be. */
export interface AppVersion {
  versionCode: number;
  versionName: string;
}

function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

function isNonEmptyString(s: unknown): s is string {
  return typeof s === 'string' && s.length > 0;
}

/**
 * Validate + parse a raw update-manifest document. Returns null on anything
 * malformed, so the caller can treat "couldn't understand the manifest" exactly
 * like "no manifest" and simply not offer an update (never crash, never prompt
 * on garbage). apkUrl must be an https URL — we won't download over plain http.
 */
export function parseUpdateManifest(data: unknown): UpdateManifest | null {
  if (!data || typeof data !== 'object') return null;
  const r = data as Record<string, unknown>;
  if (!isFiniteNumber(r.versionCode)) return null;
  if (!isNonEmptyString(r.versionName)) return null;
  if (!isNonEmptyString(r.apkUrl)) return null;
  if (!/^https:\/\//i.test(r.apkUrl)) return null;

  const out: UpdateManifest = {
    versionCode: r.versionCode,
    versionName: r.versionName,
    apkUrl: r.apkUrl,
  };
  // notes is optional; keep it only if it's a real string.
  if (isNonEmptyString(r.notes)) out.notes = r.notes;
  return out;
}

/**
 * True when `manifest` describes a build newer than what's running. Comparison is
 * purely on versionCode (the monotonic build number), so a re-published build
 * with the same code is NOT offered again, and a lower code (e.g. a rolled-back
 * manifest) is ignored rather than trying to "update" downward.
 */
export function isUpdateAvailable(current: AppVersion, manifest: UpdateManifest): boolean {
  return manifest.versionCode > current.versionCode;
}

/**
 * The single decision the UI needs: given the running version and a freshly
 * fetched (already-parsed) manifest, either there's an update to offer or there
 * isn't. Kept as one function so the store just stores what this returns.
 */
export interface UpdateCheck {
  available: boolean;
  /** The manifest, present only when available so the UI has the notes + apkUrl. */
  manifest?: UpdateManifest;
}

export function decideUpdate(
  current: AppVersion,
  manifest: UpdateManifest | null,
): UpdateCheck {
  if (manifest && isUpdateAvailable(current, manifest)) {
    return { available: true, manifest };
  }
  return { available: false };
}
